import type { DiffTaxonomyImportResponse } from '@/api/api';
import { useDiffTaxonomyImport } from '@/hooks/api/taxonomy/useDiffTaxonomyImport';
import { useImportTaxonomyNodes } from '@/hooks/api/taxonomy/useImportTaxonomyNodes';
import { useReimportTaxonomyNodes } from '@/hooks/api/taxonomy/useReimportTaxonomyNodes';
import { getApiError } from '@/types/apiError';
import { pluralize } from '@/utils/format';
import { Dialog, DialogTitle } from '@mui/material';
import { useState } from 'react';
import { ImportConflictStep } from './ImportConflictStep';
import { ImportPreviewStep } from './ImportPreviewStep';
import { ImportResultStep } from './ImportResultStep';
import { ImportUploadStep } from './ImportUploadStep';
import type { ParsedTaxonomyImport } from './taxonomyImportParser';
import { TaxonomyImportPageObjectModel } from '@/poms/taxonomyImportPageObjectModel';

type Step = 'upload' | 'conflict' | 'preview' | 'result';

interface TaxonomyImportModalProperties {
  isOpen: boolean;
  onClose: () => void;
  taxonomyId: string;
}

export const TaxonomyImportModal = ({ isOpen, onClose, taxonomyId }: TaxonomyImportModalProperties) => {
  const [step, setStep] = useState<Step>('upload');
  const [parsedData, setParsedData] = useState<ParsedTaxonomyImport | undefined>();
  const [importResult, setImportResult] = useState<{ success: boolean; message: string } | undefined>();
  const [importError, setImportError] = useState<string | undefined>();
  const [diff, setDiff] = useState<DiffTaxonomyImportResponse | undefined>();
  const [diffError, setDiffError] = useState<string | undefined>();

  const { importNodes, isImporting } = useImportTaxonomyNodes(taxonomyId);
  const { diffImport, isDiffing } = useDiffTaxonomyImport(taxonomyId);
  const { reimportNodes, isReimporting } = useReimportTaxonomyNodes(taxonomyId);

  const isReimport =
    diff !== undefined && (diff.unchangedCount > 0 || diff.removals.length > 0 || diff.moves.length > 0);

  const handleClose = () => {
    if (isImporting || isReimporting) return;
    setStep('upload');
    setParsedData(undefined);
    setImportResult(undefined);
    setImportError(undefined);
    setDiff(undefined);
    setDiffError(undefined);
    onClose();
  };

  const handleFileParsed = async (data: ParsedTaxonomyImport) => {
    setParsedData(data);
    setDiffError(undefined);

    try {
      const response = await diffImport({ nodes: data.tree });
      const diffResult = response.data;
      setDiff(diffResult);

      const hasExistingNodes =
        diffResult.unchangedCount > 0 || diffResult.removals.length > 0 || diffResult.moves.length > 0;

      if (hasExistingNodes) {
        const hasChanges =
          diffResult.additions.length > 0 || diffResult.removals.length > 0 || diffResult.moves.length > 0;
        if (hasChanges) {
          setStep('conflict');
        } else {
          setImportResult({
            success: true,
            message: 'No changes detected. The taxonomy already matches the imported file.',
          });
          setStep('result');
        }
      } else {
        setStep('preview');
      }
    } catch (error: unknown) {
      const apiError = getApiError(error as Error, 'Failed to analyze import');
      setDiffError(apiError?.message ?? 'Failed to analyze import');
    }
  };

  const handleReimport = async (keepNodeIds: string[], renameMap: Record<string, string>) => {
    if (!parsedData) return;

    try {
      setImportError(undefined);
      const response = await reimportNodes({ nodes: parsedData.tree, keepNodeIds, renameMap });
      const { addedCount, removedCount, movedCount, renamedCount, deletedLabelCount } = response.data;
      const changeParts: string[] = [];
      if (addedCount > 0) changeParts.push(`${addedCount} added`);
      if (removedCount > 0) changeParts.push(`${removedCount} removed`);
      if (movedCount > 0) changeParts.push(`${movedCount} moved`);
      if (renamedCount > 0) changeParts.push(`${renamedCount} renamed`);
      const changesSummary = changeParts.length > 0 ? changeParts.join(', ') : '';
      const labelSummary =
        deletedLabelCount > 0 ? `${pluralize(deletedLabelCount, 'asset assignment', 'asset assignments')} cleared` : '';
      const messageParts = [changesSummary, labelSummary].filter(Boolean);
      const message = messageParts.length > 0 ? messageParts.join('. ') + '.' : '';
      setImportResult({
        success: true,
        message: `Taxonomy updated: ${message}`,
      });
      setStep('result');
    } catch (error: unknown) {
      const apiError = getApiError(error as Error, 'Re-import failed');
      setImportError(apiError?.message ?? 'Re-import failed');
    }
  };

  const handleImport = async () => {
    if (!parsedData) return;

    try {
      setImportError(undefined);
      const response = await importNodes({ nodes: parsedData.tree });
      setImportResult({
        success: true,
        message: `Successfully imported ${response.data.importedCount} categories.`,
      });
      setStep('result');
    } catch (error: unknown) {
      const apiError = getApiError(error as Error, 'Import failed');
      setImportError(apiError?.message ?? 'Import failed');
    }
  };

  const actionLabel = isReimport ? 'Re-import' : 'Import';

  return (
    <Dialog
      fullWidth
      maxWidth='md'
      open={isOpen}
      onClose={handleClose}
      aria-modal='true'
      slotProps={{
        paper: {
          sx: { borderRadius: '12px' },
        },
      }}
    >
      <DialogTitle data-testid={TaxonomyImportPageObjectModel.DialogTitle}>{actionLabel} Taxonomy</DialogTitle>

      {step === 'upload' && (
        <ImportUploadStep
          onFileParsed={data => void handleFileParsed(data)}
          onClose={handleClose}
          externalError={diffError}
        />
      )}

      {step === 'conflict' && diff && (
        <ImportConflictStep
          diff={diff}
          isDiffing={isDiffing}
          diffError={diffError}
          importError={importError}
          isReimporting={isReimporting}
          onBack={() => {
            setStep('upload');
            setParsedData(undefined);
            setDiff(undefined);
            setDiffError(undefined);
            setImportError(undefined);
          }}
          onReimport={(keepNodeIds, renameMap) => void handleReimport(keepNodeIds, renameMap)}
        />
      )}

      {step === 'preview' && parsedData && (
        <ImportPreviewStep
          parsedData={parsedData}
          isImporting={isImporting}
          importError={importError}
          actionLabel={actionLabel}
          onBack={() => {
            setStep('upload');
            setParsedData(undefined);
            setImportError(undefined);
          }}
          onImport={() => void handleImport()}
        />
      )}

      {step === 'result' && importResult && (
        <ImportResultStep success={importResult.success} message={importResult.message} onClose={handleClose} />
      )}
    </Dialog>
  );
};
